import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Gift, Copy, Check, Send, MessageCircle, Mail, Users, Wallet,
  Sparkles,
} from "lucide-react";
import { getMyReferrals } from "@/lib/api";
import { toast } from "sonner";

function formatGbp(pence) {
  return `£${(pence / 100).toFixed(pence % 100 === 0 ? 0 : 2)}`;
}

function StatTile({ icon: Icon, label, value, bg, testId }) {
  return (
    <div className={`nb-card p-5 ${bg} text-black`} data-testid={testId}>
      <Icon className="w-5 h-5 mb-2" strokeWidth={2.5} />
      <div className="font-display text-3xl font-black tracking-tighter leading-none mb-1">{value}</div>
      <div className="label-caps">{label}</div>
    </div>
  );
}

export default function Refer() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => { getMyReferrals().then(setData); }, []);

  const copy = async (text, label) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success(`${label} copied`);
    setTimeout(() => setCopied(false), 1800);
  };

  if (!data) {
    return <div className="p-10 font-bold text-zinc-500">Loading…</div>;
  }

  const shareMsg = `I&apos;ve been using ReviseAI for my child&apos;s GCSE revision, AI lessons, podcasts, flashcards, mock exams. Use my link for £${(data.award_pence / 100).toFixed(0)} off your first month: ${data.share_url}`;
  const plainShareMsg = shareMsg.replace(/&apos;/g, "'");

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-zinc-950" data-testid="refer-page">
      <header className="nb-border border-l-0 border-r-0 border-t-0 bg-white dark:bg-zinc-900 sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-6 py-3 flex items-center justify-between gap-3">
          <button onClick={() => navigate(-1)} data-testid="refer-back" className="nb-border rounded-xl px-3 py-2 font-bold text-sm flex items-center gap-2 hover:nb-shadow">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="font-display font-extrabold text-xl flex items-center gap-2">
            <Gift className="w-5 h-5" /> Refer a parent
          </div>
          <div className="w-16" />
        </div>
      </header>

      <div className="max-w-3xl mx-auto p-6 sm:p-10">
        <div className="mb-8">
          <div className="label-caps mb-2">Give £{data.award_pence / 100} · Get £{data.award_pence / 100}</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">
            Invite a parent,{" "}
            <span className="bg-[#FFE066] dark:bg-[#F6E05E] px-3 nb-border rounded-2xl inline-block text-black">
              earn credit
            </span>
          </h1>
          <p className="text-zinc-600 dark:text-zinc-400 font-medium mt-3 max-w-xl">
            When a friend subscribes using your code, we credit your next bill automatically.
            No catch, no expiry.
          </p>
        </div>

        {/* Stats */}
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <StatTile
            icon={Users}
            label="Friends joined"
            value={data.invited_count}
            bg="bg-[#D4C4FB] dark:bg-[#B794F4]"
            testId="stat-invited"
          />
          <StatTile
            icon={Sparkles}
            label="Subscribed"
            value={data.paid_count}
            bg="bg-[#9DFFB0] dark:bg-[#68D391]"
            testId="stat-paid"
          />
          <StatTile
            icon={Wallet}
            label="Credit earned"
            value={formatGbp(data.credit_earned_pence)}
            bg="bg-[#FFB38E] dark:bg-[#F6AD55]"
            testId="stat-credit"
          />
        </div>

        {/* Code + share */}
        <div className="nb-card p-6 sm:p-8 mb-8">
          <div className="label-caps mb-2">Your code</div>
          <div className="flex items-center gap-3 flex-wrap mb-5">
            <div
              className="font-display font-black text-4xl sm:text-5xl tracking-[0.25em] bg-[#FFE066] dark:bg-[#F6E05E] text-black nb-border rounded-2xl px-5 py-3"
              data-testid="referral-code"
            >
              {data.code}
            </div>
            <button
              onClick={() => copy(data.code, "Code")}
              data-testid="copy-code"
              className="nb-button px-4 py-3 flex items-center gap-2"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              Copy code
            </button>
          </div>

          <div className="label-caps mb-2">Share link</div>
          <div className="nb-border rounded-xl bg-white dark:bg-zinc-900 p-3 mb-4 flex items-center gap-2 flex-wrap">
            <code className="font-mono text-xs sm:text-sm break-all flex-1 min-w-0" data-testid="referral-url">
              {data.share_url}
            </code>
            <button
              onClick={() => copy(data.share_url, "Link")}
              data-testid="copy-url"
              className="nb-button px-3 py-2 text-xs flex items-center gap-1.5"
            >
              <Copy className="w-3.5 h-3.5" /> Copy
            </button>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <a
              href={`https://wa.me/?text=${encodeURIComponent(plainShareMsg)}`}
              target="_blank"
              rel="noreferrer"
              data-testid="share-whatsapp"
              className="nb-button px-3 py-3 bg-[#9DFFB0] dark:bg-[#68D391] flex items-center justify-center gap-1.5 text-black"
            >
              <MessageCircle className="w-4 h-4" strokeWidth={2.5} /> WhatsApp
            </a>
            <a
              href={`mailto:?subject=${encodeURIComponent("ReviseAI, £5 off")}&body=${encodeURIComponent(plainShareMsg)}`}
              data-testid="share-email"
              className="nb-button px-3 py-3 flex items-center justify-center gap-1.5"
            >
              <Mail className="w-4 h-4" /> Email
            </a>
            <a
              href={`sms:?&body=${encodeURIComponent(plainShareMsg)}`}
              data-testid="share-sms"
              className="nb-button px-3 py-3 flex items-center justify-center gap-1.5"
            >
              <Send className="w-4 h-4" /> SMS
            </a>
          </div>
        </div>

        {/* Invited list */}
        <div className="mb-6">
          <h2 className="font-display text-2xl font-extrabold mb-3">Your invites</h2>
          {data.invited_count === 0 ? (
            <div className="nb-card p-8 text-center">
              <Gift className="w-10 h-10 mx-auto mb-2" strokeWidth={2} />
              <div className="font-display text-lg font-extrabold mb-1">No invites yet</div>
              <p className="text-sm text-zinc-600 dark:text-zinc-400 font-medium">
                Share your code with another parent, when they subscribe you both get £{data.award_pence / 100} off.
              </p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="referral-invites">
              {data.invited.map((inv, i) => (
                <motion.div
                  key={inv.joined_at || `inv-${i}`}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                  className="nb-card p-4 flex items-center justify-between gap-3"
                  data-testid={`invite-row-${i}`}
                >
                  <div className="font-bold">{inv.first_name}</div>
                  <div
                    className={`text-xs font-extrabold uppercase tracking-wider px-2 py-1 nb-border rounded-full ${
                      inv.awarded_at
                        ? "bg-[#9DFFB0] dark:bg-[#68D391] text-black"
                        : inv.subscribed
                          ? "bg-[#FFE066] dark:bg-[#F6E05E] text-black"
                          : "bg-white dark:bg-zinc-900"
                    }`}
                  >
                    {inv.awarded_at ? "+£5 credited" : inv.subscribed ? "Subscribed" : "Joined"}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {data.referred_by_first && (
          <div className="nb-border rounded-xl p-4 bg-[#FFE066] dark:bg-[#F6E05E] text-black text-sm font-bold flex items-center gap-2" data-testid="referred-by-banner">
            <Sparkles className="w-4 h-4" strokeWidth={3} />
            Referred by {data.referred_by_first}, thanks for spreading the word!
          </div>
        )}
      </div>
    </div>
  );
}
